/** Safe organisation-agent projection. Credential material is intentionally absent. */
export interface AgentConfigView {
  provider?: string
  model?: string
  llmMode?: string
  temperature?: number
}

export interface AgentMetricsView {
  status?: string
  tasksCompleted?: number
  confidence?: number
  uptimePercent?: number
  errorRate?: number
  lastActiveAt?: string
}

export interface AgentView {
  /** Server `id` is the stable legacy agent slug used by agent-settings. */
  id: string
  name: string
  description?: string
  color?: string
  iconName?: string
  boardColumn?: string
  category?: string
  isConfigured: boolean
  config?: AgentConfigView
  metrics?: AgentMetricsView
}

const objectOf = (value: unknown): Record<string, unknown> | null =>
  typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : null

const stringOf = (value: unknown): string | undefined =>
  typeof value === 'string' && value !== '' ? value : undefined

const numberOf = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined

/**
 * Narrows an untrusted server row to the display-safe surface. Deliberately
 * reconstructing the nested config prevents secret-shaped keys from reaching
 * feature state even if a future server response accidentally serializes one.
 */
export const asAgentView = (value: unknown): AgentView | null => {
  const row = objectOf(value)
  const id = row === null ? undefined : stringOf(row.id)
  const name = row === null ? undefined : stringOf(row.name)
  if (id === undefined || name === undefined || row === null) return null

  const config = objectOf(row.config)
  const metrics = objectOf(row.metrics)
  const safeConfig =
    config === null
      ? undefined
      : {
          provider: stringOf(config.provider),
          model: stringOf(config.model),
          llmMode: stringOf(config.llmMode),
          temperature: numberOf(config.temperature),
        }
  const safeMetrics =
    metrics === null
      ? undefined
      : {
          status: stringOf(metrics.status),
          tasksCompleted: numberOf(metrics.tasksCompleted),
          confidence: numberOf(metrics.confidence),
          uptimePercent: numberOf(metrics.uptimePercent),
          errorRate: numberOf(metrics.errorRate),
          lastActiveAt: stringOf(metrics.lastActiveAt),
        }

  return {
    id,
    name,
    description: stringOf(row.description),
    color: stringOf(row.color),
    iconName: stringOf(row.iconName),
    boardColumn: stringOf(row.boardColumn),
    category: stringOf(row.category),
    isConfigured: row.isConfigured === true,
    ...(safeConfig === undefined ? {} : { config: safeConfig }),
    ...(safeMetrics === undefined ? {} : { metrics: safeMetrics }),
  }
}
